import { ArrowRight, Menu, X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { Suspense, useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router";

import { getLabel, IDENTITY_PHRASE, LocaleProvider, NAV_LABELS } from "@/lib/i18n";
import type { Locale } from "@/types/content";

export function Root() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  // TODO: Extract locale from URL once routing is updated
  // For now, default to 'de'
  const locale: Locale = "de";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const navLinks = [
    { name: getLabel(NAV_LABELS, "studioHall", locale), path: "/studio" },
    { name: getLabel(NAV_LABELS, "events", locale), path: "/events" },
    { name: getLabel(NAV_LABELS, "blog", locale), path: "/news" },
    { name: getLabel(NAV_LABELS, "artistGallery", locale), path: "/residents" },
    { name: getLabel(NAV_LABELS, "about", locale), path: "/about" },
  ];

  const utilityLinks = [
    { name: "Dashboard", path: "/dashboard" },
    { name: "Calendar", path: "/events" },
    { name: "Videos", path: "/events?filter=videos" },
    { name: "Contact", path: "/contact" },
  ];

  return (
    <LocaleProvider locale={locale}>
      <div className="flex min-h-screen flex-col bg-black font-sans text-[#f4f4f4]">
        {/* Header - Brutalist & Structural */}
        <header
          className={`fixed top-0 z-50 w-full border-b transition-all duration-300 ${
            isScrolled
              ? "border-white/20 bg-black py-4"
              : "border-white/10 bg-black/90 py-6 backdrop-blur-sm"
          }`}
        >
          <div className="container mx-auto flex items-center justify-between px-6 md:px-12">
            <NavLink to="/" className="z-50 font-serif text-3xl font-bold tracking-tighter">
              o-ton
            </NavLink>

            {/* Desktop Nav */}
            <nav className="hidden items-center gap-8 text-xs tracking-[0.15em] text-white/60 uppercase lg:flex">
              {navLinks.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  className={({ isActive }) =>
                    `transition-colors duration-300 hover:text-white ${isActive ? "border-b border-white pb-1 text-white" : "border-b border-transparent pb-1"}`
                  }
                >
                  {link.name}
                </NavLink>
              ))}
            </nav>

            <div className="z-50 hidden items-center gap-6 lg:flex">
              <NavLink
                to="/dashboard"
                className="text-xs font-medium tracking-[0.15em] text-white/60 uppercase transition-colors hover:text-white"
              >
                Dashboard
              </NavLink>
              <NavLink
                to="/contact"
                className="rounded-none border border-white/40 px-6 py-3 text-xs font-medium tracking-[0.15em] uppercase transition-all duration-300 hover:bg-white hover:text-black"
              >
                {getLabel(NAV_LABELS, "contact", locale)}
              </NavLink>
            </div>

            {/* Mobile Menu Toggle */}
            <button
              className="z-50 text-white lg:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X size={28} strokeWidth={1} />
              ) : (
                <Menu size={28} strokeWidth={1} />
              )}
            </button>
          </div>
        </header>

        {/* Mobile Menu Overlay */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: "-100%" }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: "-100%" }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="fixed inset-0 z-40 flex flex-col border-b border-white/20 bg-black px-6 pt-32 pb-10"
            >
              <nav className="mb-12 flex flex-col gap-6 font-serif text-4xl tracking-tighter uppercase">
                {navLinks.map((link) => (
                  <NavLink
                    key={link.path}
                    to={link.path}
                    className="border-b border-white/10 pb-4 transition-colors hover:text-white/60"
                  >
                    {link.name}
                  </NavLink>
                ))}
                <NavLink
                  to="/contact"
                  className="mt-4 border border-white p-6 text-center text-xl text-white"
                >
                  {getLabel(NAV_LABELS, "contact", locale)}
                </NavLink>
              </nav>
              <div className="mt-auto flex flex-wrap gap-6 text-xs tracking-[0.15em] text-white/50 uppercase">
                {utilityLinks.map((link) => (
                  <a
                    key={link.name}
                    href={link.path}
                    className="transition-colors hover:text-white"
                  >
                    {link.name}
                  </a>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Content */}
        <main className="flex flex-grow flex-col pt-[88px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-grow flex-col"
            >
              <Suspense
                fallback={
                  <div className="flex min-h-[50vh] items-center justify-center">
                    <div className="text-sm tracking-[0.2em] text-white/60 uppercase">
                      Loading...
                    </div>
                  </div>
                }
              >
                <Outlet />
              </Suspense>
            </motion.div>
          </AnimatePresence>
        </main>

        {/* Footer - Harsh & Structured */}
        <footer className="mt-auto border-t border-white/20 bg-black pt-24 pb-12">
          <div className="container mx-auto grid grid-cols-1 gap-12 px-6 md:grid-cols-12 md:gap-8 md:px-12">
            <div className="flex flex-col justify-between md:col-span-4">
              <div>
                <NavLink
                  to="/"
                  className="mb-6 block font-serif text-5xl font-bold tracking-tighter"
                >
                  o-ton
                </NavLink>
                <p className="max-w-[300px] text-sm leading-relaxed tracking-wide text-white/70">
                  {IDENTITY_PHRASE[locale]}
                </p>
              </div>
            </div>

            <div className="md:col-span-2">
              <h4 className="mb-8 border-b border-white/20 pb-4 text-xs tracking-[0.15em] text-white/40 uppercase">
                Explore
              </h4>
              <ul className="flex flex-col gap-4 text-xs tracking-widest text-white/80 uppercase">
                {navLinks.map((link) => (
                  <li key={link.path}>
                    <NavLink to={link.path} className="transition-all hover:pl-2 hover:text-white">
                      {link.name}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>

            <div className="md:col-span-2">
              <h4 className="mb-8 border-b border-white/20 pb-4 text-xs tracking-[0.15em] text-white/40 uppercase">
                Utilities
              </h4>
              <ul className="flex flex-col gap-4 text-xs tracking-widest text-white/80 uppercase">
                {utilityLinks.map((link) => (
                  <li key={link.name}>
                    <a href={link.path} className="transition-all hover:pl-2 hover:text-white">
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div className="md:col-span-4">
              <h4 className="mb-8 border-b border-white/20 pb-4 text-xs tracking-[0.15em] text-white/40 uppercase">
                Dispatch
              </h4>
              <p className="mb-6 text-xs tracking-widest text-white/60 uppercase">
                Subscribe for event updates and cultural news.
              </p>
              <form className="group flex border border-white/30 p-1 transition-colors hover:border-white">
                <input
                  type="email"
                  placeholder="EMAIL ADDRESS"
                  className="w-full bg-transparent px-4 text-xs tracking-widest text-white uppercase outline-none placeholder:text-white/30"
                />
                <button
                  type="submit"
                  className="px-4 py-2 text-white/60 transition-all hover:bg-white hover:text-black"
                >
                  <ArrowRight size={18} />
                </button>
              </form>
            </div>
          </div>

          <div className="container mx-auto mt-24 flex flex-col items-center justify-between gap-6 border-t border-white/20 px-6 pt-8 text-[10px] tracking-[0.2em] text-white/40 uppercase md:flex-row md:px-12">
            <p>&copy; {new Date().getFullYear()} o-ton cologne. all rights reserved.</p>
            <div className="flex gap-8">
              <a href="#" className="transition-colors hover:text-white">
                Instagram
              </a>
              <a href="#" className="transition-colors hover:text-white">
                Imprint
              </a>
              <a href="#" className="transition-colors hover:text-white">
                Privacy
              </a>
            </div>
          </div>
        </footer>
      </div>
    </LocaleProvider>
  );
}
